import { useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { Line, Text, OrbitControls, Sphere } from '@react-three/drei'
import * as THREE from 'three'

const eternitDatabase = 
[
  {
    name: "Eternit Estándar",
    width: 0.76,                // Ancho (m)
    length: 2.13,                // Largo (m)
    thickness: .008,            //Espesor del eternit   
    maxSupportLenght : 0.66 ,   // Separación máxima de soporte
    heightOverlap: 0.15,         // Traslape (m) 
    widthOverlap: 0.075,         // Traslape (m)
    holeOffset: 0.075,             // Distancia de perforaciones a los filos (m)
    supportsPerPanel : 3,
  },
];

const roundTo2 = (num) => Math.round(num * 100) / 100;
const roundTo4 = (num) => Math.round(num * 10000) / 10000;

const RoofGrid = (
{ 
  length,
  width,
  peakHeight,
  slope,
  showLabels,
  eternitType,
  params,
  setParams
}) => 
{
  // Altura base fija en 0
  const baseHeight = 0; 
  const selectedEternit = eternitDatabase[eternitType];

  const heightFactor = peakHeight / width;
  const hypotenuse = roundTo4(Math.sqrt(width * width + peakHeight * peakHeight)); 
  const hypotenuseFactor = roundTo4(width/hypotenuse);
  const slopeRadians =  ((slope - 0.5) * Math.PI / 180);
  const transverseRadians =  ((0.8) * Math.PI / 180);

  // Calcular espaciamiento en eje Z (máximo 3m)
  const maxGirderSpacing = 3
  const girderSpacing = Math.min(maxGirderSpacing, length / Math.ceil(length / maxGirderSpacing))
  const girderPoints = Array.from({length: Math.ceil(roundTo4(length / girderSpacing)) + 1}, (_, i) => Math.min(roundTo4(i * girderSpacing, length)));

  // Puntos de grid (1 eje X, 3 ejes Y)
  const zPoints = [0, length];
  const xPoints = [0, width];
  
  const lines = [];
  const labels = [];
  const intercalatedGuides = [];
  const panels = [];

  const lineColor = "#B0B0B0";
  const mainGirders = '#e413ea';
  
  const suportMaxSupportLenghtPlaneProjection = roundTo4((selectedEternit.maxSupportLenght - .02)* hypotenuseFactor);
  const supportSpacing = Math.min(suportMaxSupportLenghtPlaneProjection, width / Math.ceil(width / suportMaxSupportLenghtPlaneProjection));

  const supportPoints = Array.from({length: Math.ceil(roundTo4(width / supportSpacing)) + 1}, (_, i) => Math.min(roundTo4(i * supportSpacing), width));

  //Información del Panel Seleccionado
  const supportsPerPanel = selectedEternit.supportsPerPanel;

  let supportCounter = 0;
  const mainSupportsArray = [0]
  
  supportPoints.forEach((z) => 
  {
    if(supportCounter === supportsPerPanel)
    {      
      mainSupportsArray.push(z);
      supportCounter = 0;
    }

    supportCounter +=1;   
  });

  if(!mainSupportsArray.includes(width)) mainSupportsArray.push(width);

  // =============================================
  // Vigas Superior e Inferior
  // =============================================
  lines.push(
    <Line
      key = {`viga inferior en el eje z`}
      points = 
      {[
        [0, 0, 0],
        [0, 0, length]
      ]}
      color = {mainGirders}
      lineWidth = {2}
    />);

  lines.push(
    <Line
      key = {`viga superior en el eje z`}
      points = 
      {[
        [width, peakHeight, 0],
        [width, peakHeight, length]
      ]}
      color = {mainGirders}
      lineWidth = {2}
    />);

  // =============================================
  // Malla en el eje X - Soportes de las Correas
  // =============================================
  girderPoints.forEach((z, i) => 
  {
    const selectedColor = z === 0 || z === length ? mainGirders : lineColor;

    lines.push(
      <Line
        key = {`y-line-${i}`}
        points = 
        {[
          [xPoints[0], baseHeight, z],
          [xPoints[1], peakHeight, z]
        ]}
        color = {selectedColor}
        lineWidth = {1}
      />
    )

    // Etiquetas para eje Y
    showLabels && labels.push(
      <Text
        key={`y-label-${i}`}
        position={[xPoints[0] - 1, baseHeight - 0.5, z]}
        fontSize={0.2}
        color="black"
        anchorX="center"
        anchorY="middle"
      >
        {i === 0 ?  "" : `${z.toFixed(2)}m`}
      </Text>
    )
  })  
  
  // =============================================
  // Líneas principales en X - Línea de separación de Correas o apoyos de el eternit
  // =============================================
  supportPoints.forEach((x, i) => 
  {
    lines.push(
      <Line
        key={`z-line-${i}`}
        points={[
          [x, baseHeight, xPoints[0]],
          [x, i === 0 ? baseHeight : peakHeight, xPoints[0]]
        ]}
        color={'#ec1919'}
        lineWidth={2}
      />
    )

    // Etiquetas para eje Z
    showLabels && labels.push(
      <Text
        key = {`z-label-${i}`}
        position = {[x, i === 0 ? baseHeight : peakHeight + 0.4, xPoints[0]]}
        fontSize = {0.2}
        color = "black"
        anchorX = "center"
        anchorY = "middle"
      >
        {i === 0 ? "Base" : `${x.toFixed(2)}\nm`}
      </Text>
    )
  })

  // =============================================
  // Correas en el Eje Z
  // =============================================
  const intercalatedColor = "#16e144" // Color para líneas intercaladas

  for (let i = 1; i <= supportPoints.length - 2; i++) 
  {   
    const currentHeight = heightFactor * supportPoints[i];

    // Línea horizontal intercalada
    intercalatedGuides.push(
      <Line
        key = {`intercalated-horizontal-${i}`}
        points = 
        {[
          [supportPoints[i], currentHeight, 0],
          [supportPoints[i], currentHeight, length]
        ]}
        color = {intercalatedColor}
        lineWidth = {mainSupportsArray.includes(supportPoints[i]) ? 2.5 : 1.2}
        dashed = {mainSupportsArray.includes(supportPoints[i]) ? false : true}
        dashSize = {0.15}
        gapSize = {0.1}
      />
    )    
  }

  const maxZPanels = Math.floor(length/(selectedEternit.width - selectedEternit.widthOverlap));

  let completePanels = 0;

  for (let y = 0; y <= mainSupportsArray.length - 2; y++)
  {    
    let panelLength; // Largo real de la plancha (eje X);
    let RowX;
    let RowY;

    if(y !== mainSupportsArray.length-2)
    {
      panelLength = selectedEternit.length; // Largo real de la plancha (eje X);

      RowX = ((panelLength/2 - selectedEternit.heightOverlap)) * hypotenuseFactor + mainSupportsArray[y];
      RowY = RowX * heightFactor;
    }
    else
    {
      panelLength = (mainSupportsArray[mainSupportsArray.length-1] - mainSupportsArray[mainSupportsArray.length-2]) + selectedEternit.heightOverlap * 2;
      RowX = ((panelLength/2 - selectedEternit.heightOverlap)) * hypotenuseFactor + mainSupportsArray[y];
      RowY = RowX * heightFactor;
    }
    
    for (let i = 0; i <= maxZPanels; i++)
    {
      let lastPanelWidth = 0;
      let zPos;
      let panelWidth = selectedEternit.width;   // Ancho real (eje Z)

      if(i === maxZPanels )
      {
        lastPanelWidth = roundTo2(length - (selectedEternit.width - selectedEternit.widthOverlap) * i);
        panelWidth = Math.min(lastPanelWidth, selectedEternit.width);
        zPos = panelWidth/2 + (selectedEternit.width - selectedEternit.widthOverlap) * i;      
      }
      else
      {
        panelWidth = selectedEternit.width;
        zPos = panelWidth/2 + (panelWidth - selectedEternit.widthOverlap) * i;
        
        completePanels += 1;
      }

      panels.push(
        <group >
          <mesh
            position={[RowX , RowY, zPos]}
            rotation={[transverseRadians, 0, slopeRadians]} // Rotación en Z para la pendiente
          >
            <boxGeometry args={[panelLength, selectedEternit.thickness, panelWidth]} />
            <meshStandardMaterial 
              color="#e67217" 
              transparent={true}
              opacity={0.7}
              side={THREE.DoubleSide}
            />
          </mesh>

          {/* Filo negro alrededor del panel */}
          <lineSegments 
            position={[RowX , RowY, zPos]}
            rotation={[transverseRadians, 0, slopeRadians]} // Rotación en Z para la pendiente
          >
            <edgesGeometry args={[new THREE.BoxGeometry(panelLength, selectedEternit.thickness, panelWidth)]} />
            <lineBasicMaterial color="#000000" linewidth={1} />
          </lineSegments>
        </group>
      );
    }
  }
  
  if (completePanels !== params.completePanels)
  {
    setParams(prev => ({
      ...prev,
      completePanels: completePanels
    }));
  }

  return (
    <group>
      {lines}
      {labels}
      {intercalatedGuides}
      {panels}
      
      {showLabels && (
        <Text
          position={[width/2, peakHeight + 1, zPoints[0] - 2]}
          fontSize={0.8}
          color="#1e2222"
          anchorX="center"
          anchorY="middle"
        >
          Pendiente: {slope.toFixed(1)}° | Altura: {peakHeight.toFixed(2)}m
        </Text>
      )}
    </group>
  )
}

const RoofPanels = () => 
{
  // Estado para los parámetros del techo
  const [params, setParams] = useState({
    length: 8,          //Longitud de la Estructura
    width: 6,           //Ancho de la Estructura
    peakHeight: 0.42,
    slope: 4,           // Pendiente calculada inicial
    showLabels: true,
    completePanels: 0
  })

  // Manejadores de cambios
  const handleChange = (e) => 
  {
    const { name, value } = e.target
    const newValue = name === 'showLabels' ? !params.showLabels : parseFloat(value);
    
    setParams(prev => 
    {
      let updatedParams = { ...prev, [name]: newValue }
      
      if (name === 'peakHeight')
      {
        // Al cambiar altura, recalcular pendiente (longitud constante)
        updatedParams.slope = Math.atan(updatedParams.peakHeight / updatedParams.width) * (180/Math.PI)
      } 
      else if (name === 'width')
      {
        // Al cambiar pendiente, recalcular altura (longitud constante)
        updatedParams.peakHeight = updatedParams.width * Math.tan(updatedParams.slope * Math.PI / 180)
      }
      else if (name === 'slope')
      {
        // Al cambiar pendiente, recalcular altura (longitud constante)
        updatedParams.peakHeight = updatedParams.width * Math.tan(updatedParams.slope * Math.PI / 180)
      }
      
      return updatedParams
    })
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {/* Panel de controles */}
      <div style={{ 
        width: '300px', 
        padding: '20px', 
        background: '#f5f5f5',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>Configuración del Techo</h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label>Longitud (m): </label>
          <input
            type="number"
            name="length"
            value={params.length.toFixed(2)}
            onChange={handleChange}
            min="3"
            max="30"
            step="0.1"
            style={{ width: '100%', padding: '5px' }}
          />
          <div style={{ fontSize: '0.8em', color: '#999' }}>
            (Longitud de la Estructura)
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label>Ancho (m): </label>
          <input
            type="number"
            name="width"
            value={params.width.toFixed(2)}
            onChange={handleChange}
            min="1"
            max="30"
            step="0.1"
            style={{ width: '100%', padding: '5px' }}
          />
          <div style={{ fontSize: '0.8em', color: '#999' }}>
            (Ancho de la Estructura)
          </div>
        </div>       

        <div style={{ marginBottom: '20px' }}>
          <label>Altura de cúspide (m): </label>
          <input
            type="number"
            name="peakHeight"
            value={params.peakHeight.toFixed(2)}
            onChange={handleChange}
            min="0.1"
            max="10"
            step="0.05"
            style={{ width: '100%', padding: '5px' }}
          />
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            (Al cambiar, se ajusta la pendiente)
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label>Pendiente (°): </label>
          <input
            type="number"
            name="slope"
            value={params.slope.toFixed(1)}
            onChange={handleChange}
            min="2"
            max="45"
            step=".5"
            style={{ width: '100%', padding: '5px' }}
          />
          <div style={{ fontSize: '0.8em', color: '#666' }}>
            (Al cambiar, se ajusta la altura)
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label>
            <input
              type="checkbox"
              name="showLabels"
              checked={params.showLabels}
              onChange={handleChange}
              style={{ marginRight: '5px' }}
            />
            Mostrar etiquetas
          </label>
        </div>

        <div style={{ 
          padding: '10px', 
          background: '#eee', 
          borderRadius: '5px',
          fontSize: '0.9em'
        }}>
          <div><strong>Relación actual:</strong></div>
          <div>Altura = {params.peakHeight.toFixed(2)}m</div>
          <div>Pendiente = {params.slope.toFixed(1)}°</div>
          <div>Longitud fija = {params.length.toFixed(2)}m</div>
          <div>Paneles Completos = {params.completePanels}</div>
        </div>
      </div>

      {/* Vista 3D */}
      <div style={{ flex: 1 }}>
        <Canvas
          camera={{ position: [1.20, 2.82, 10.86]}}
        >          
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 20, 10]} intensity={1} />          
          
          <OrbitControls
            enableZoom={true} // Permite hacer zoom
            enablePan={true} // Evita el desplazamiento lateral
            minPolarAngle={Math.PI / 6} // Limita la rotación hacia abajo
            maxPolarAngle={Math.PI / 2} // Evita que la cámara rote completamente
            target={[params.width/2, 0, params.length/2]} // Ahora el pivot de rotación es el centro del grid
          />          
          
          {/* Ejes de coordenadas */}
          <axesHelper args={[1]} position={new THREE.Vector3(...[params.width/2, 0, params.length/2])} />

          <Sphere args={[0.1, 16, 16]} position={[params.width/2, 0, params.length/2]}>
            <meshStandardMaterial color="red" emissive="red" emissiveIntensity={0.5} />
          </Sphere>
          
          {/* Grid del techo */}
          <RoofGrid 
            length      = {params.length}
            width       = {params.width}
            peakHeight  = {params.peakHeight}
            slope       = {params.slope}
            showLabels  = {params.showLabels}
            eternitType = {0}
            completePanels = {params.completePanels}
            params      = {params}
            setParams   = {setParams}
          />
        </Canvas>
      </div>
    </div>
  )
}

export default RoofPanels